import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LazyLoad from 'react-lazyload';
import { allListings, listingTypes } from '../data/listings';
import { generateStructuredData, generatePageTitle, generateMetaDescription, generateBreadcrumbSchema } from '../utils/seoUtils';

const CategoryPage = () => {
    const { category } = useParams();
    const [filteredListings, setFilteredListings] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState('all');

    const categoryName = listingTypes[category] || category;

    useEffect(() => {
        let currentListings = allListings.filter(listing => listing.listingType === category);

        if (selectedProvince !== 'all') {
            currentListings = currentListings.filter(listing => listing.province === selectedProvince);
        }

        currentListings.sort((a, b) => {
            if (a.isFeatured && !b.isFeatured) return -1;
            if (!a.isFeatured && b.isFeatured) return 1;
            if (a.tier !== b.tier) return a.tier - b.tier;
            if (a.rating !== b.rating) return b.rating - a.rating;
            return a.name.localeCompare(b.name);
        });

        setFilteredListings(currentListings);
    }, [category, selectedProvince]);

    const allProvinces = [...new Set(allListings
        .filter(listing => listing.listingType === category)
        .map(listing => listing.province))].sort();

    const breadcrumbs = [
        { name: 'Inicio', url: '/' },
        { name: categoryName, url: `/categoria/${category}` }
    ];

    const renderListingCard = (listing) => {
        return (
            <article key={listing.id} className="premium-listing-card group">
                <div className="relative h-40 bg-slate-100 overflow-hidden">
                    <img
                        src={`https://images.unsplash.com/photo-1560750588-73207b1ef5b8?auto=format&fit=crop&w=800&q=80`}
                        alt={listing.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {listing.isFeatured && (
                        <div className="absolute top-3 right-3 z-10">
                            <span className="badge-tier-1 text-[8px]">Destacado</span>
                        </div>
                    )}
                </div>
                <div className="p-5">
                    <Link to={`/centro/${listing.id}`}>
                        <h3 className="text-lg font-bold text-slate-900 group-hover:text-rose-600 transition-colors line-clamp-1 mb-2">
                            {listing.name}
                        </h3>
                    </Link>
                    <p className="text-xs text-slate-500 mb-4">{listing.city}, {listing.province}</p>
                    <div className="flex items-center justify-between">
                        <div className="flex text-amber-400">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className={`w-3 h-3 ${i < Math.floor(listing.rating) ? 'fill-current' : 'text-slate-200'}`} viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>
                        <Link to={`/centro/${listing.id}`} className="text-xs font-bold text-rose-600">Detalles →</Link>
                    </div>
                </div>
            </article>
        );
    };

    return (
        <div className="pb-32">
            <Helmet>
                <title>{generatePageTitle.category(categoryName, selectedProvince !== 'all' ? selectedProvince : null)}</title>
                <meta name="description" content={generateMetaDescription.category(categoryName, filteredListings.length, selectedProvince !== 'all' ? selectedProvince : null)} />
                <link rel="canonical" href={`https://esteticasdeargentina.com/categoria/${category}`} />
                <script type="application/ld+json">
                    {JSON.stringify(generateStructuredData.categoryPage(categoryName, filteredListings))}
                </script>
            </Helmet>

            <header className="bg-mesh pt-32 pb-20">
                <div className="container mx-auto px-6">
                    <nav className="mb-6">
                        <ol className="flex items-center space-x-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                            <li><Link to="/" className="hover:text-rose-600 transition-colors">Inicio</Link></li>
                            <li><span className="mx-2">/</span></li>
                            <li className="text-slate-900">{categoryName}</li>
                        </ol>
                    </nav>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        <span className="text-gradient">{categoryName}</span> en Argentina
                    </h1>
                    <p className="text-slate-600 max-w-2xl">
                        Explora los mejores centros especializados en {categoryName.toLowerCase()}. Calidad, seguridad y resultados garantizados.
                    </p>
                </div>
            </header>

            <main className="container mx-auto px-6 py-12">
                <div className="lg:flex gap-12">
                    {/* Filters Sidebar */}
                    <aside className="lg:w-1/4 mb-12 lg:mb-0">
                        <div className="glass-card p-6 sticky top-28">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-6 font-display">Filtrar por Provincia</h2>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setSelectedProvince('all')}
                                    className={`w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedProvince === 'all' ? 'bg-rose-600 text-white shadow-lg shadow-rose-200' : 'text-slate-600 hover:bg-slate-50'}`}
                                >
                                    Todas las provincias
                                </button>
                                {allProvinces.map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setSelectedProvince(p)}
                                        className={`w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedProvince === p ? 'bg-rose-600 text-white shadow-lg shadow-rose-200' : 'text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>

                    {/* Results Grid */}
                    <div className="lg:w-3/4">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-xl font-bold text-slate-900">Resultados encontrados ({filteredListings.length})</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                            {filteredListings.map(listing => (
                                <LazyLoad key={listing.id} height={350} offset={200}>
                                    {renderListingCard(listing)}
                                </LazyLoad>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CategoryPage;